import React, { useEffect, useState } from 'react';
import { Radio, Search, Filter, RefreshCw, Bell } from 'lucide-react';
import { RoteiroOperacionalAtivo, OperacionalStats, OperacionalFiltro } from '../types/operacional';
import { operacionalService } from '../services/operacionalService';
import { supabase } from '../lib/supabase';
import { OperacionalHeaderStats } from '../components/operacional/OperacionalHeaderStats';
import { MapaMotoristasOperacional } from '../components/operacional/MapaMotoristasOperacional';
import { ListaRoteirosAtivos } from '../components/operacional/ListaRoteirosAtivos';
import { OcorrenciaOperacionalModal } from '../components/operacional/OcorrenciaOperacionalModal';
import { NotificarMotoristaModal } from '../components/operacional/NotificarMotoristaModal';

export function OperacionalPage() {
  const [roteiros, setRoteiros] = useState<RoteiroOperacionalAtivo[]>([]);
  const [filteredRoteiros, setFilteredRoteiros] = useState<RoteiroOperacionalAtivo[]>([]);
  const [stats, setStats] = useState<OperacionalStats>({
    motoristas_em_rota: 0,
    veiculos_em_movimento: 0,
    paradas_concluidas_hoje: 0,
    paradas_pendentes_hoje: 0,
    distancia_total_km: 0,
    alertas_ocorrencias: 0,
  });

  const [loading, setLoading] = useState(true);
  const [selectedRoteiroId, setSelectedRoteiroId] = useState<number | null>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  
  // Modal Ocorrência
  const [isOcorrenciaModalOpen, setIsOcorrenciaModalOpen] = useState(false);
  const [roteiroOcorrencia, setRoteiroOcorrencia] = useState<RoteiroOperacionalAtivo | null>(null);

  // Modal Notificar Motorista
  const [isNotificarModalOpen, setIsNotificarModalOpen] = useState(false);
  const [roteiroNotificar, setRoteiroNotificar] = useState<RoteiroOperacionalAtivo | null>(null);

  // Filtros
  const [filtro, setFiltro] = useState<OperacionalFiltro>({
    busca: '',
    status_motorista: 'todos',
    status_roteiro: 'todos',
    veiculo_tipo: 'todos',
  });

  const carregarDados = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const dados = await operacionalService.getRoteirosOperacionaisAtivos();
      setRoteiros(dados);
      setStats(operacionalService.calcularEstatisticas(dados));
    } catch (e) {
      console.error('Erro ao carregar dados operacionais:', e);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();

    // Polling a cada 15 segundos para atualizar as localizações GPS e status
    const interval = setInterval(() => {
      carregarDados(false);
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  // Aplica filtros
  useEffect(() => {
    const filtrados = operacionalService.filtrarRoteiros(roteiros, filtro);
    setFilteredRoteiros(filtrados);
  }, [roteiros, filtro]);

  const handleOpenOcorrenciaModal = (roteiro: RoteiroOperacionalAtivo) => {
    setRoteiroOcorrencia(roteiro);
    setIsOcorrenciaModalOpen(true);
  };

  const handleOpenNotificarModal = (roteiro: RoteiroOperacionalAtivo) => {
    setRoteiroNotificar(roteiro);
    setIsNotificarModalOpen(true);
  };

  const showToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 4000);
  };

  const handleSalvarOcorrencia = async (roteiroId: number, tipo: string, descricao: string): Promise<boolean> => {
    const roteiro = roteiros.find(r => r.id === roteiroId);
    // Uma ocorrência precisa estar vinculada a uma OS real (tabela ocorrencias_os).
    // Usamos a parada ativa (ou a primeira) do roteiro como referência.
    const paradaRef = roteiro?.paradas.find(p => p.status_parada !== 'concluida') || roteiro?.paradas[0];
    const ordemDeServicoId = paradaRef?.ordem_de_servico_id;

    if (!ordemDeServicoId) {
      console.error('Não foi possível determinar a OS vinculada ao roteiro', roteiroId);
      return false;
    }

    const { error } = await supabase.from('ocorrencias_os').insert({
      ordem_de_servico_id: ordemDeServicoId,
      tipo_evento: tipo,
      descricao,
    });

    if (error) {
      console.error('Erro ao registrar ocorrência:', error);
      return false;
    }

    setRoteiros(prev => prev.map(r => {
      if (r.id === roteiroId) {
        return {
          ...r,
          ocorrencias_count: (r.ocorrencias_count || 0) + 1,
          ultima_ocorrencia: descricao
        };
      }
      return r;
    }));

    setStats(prev => ({
      ...prev,
      alertas_ocorrencias: prev.alertas_ocorrencias + 1
    }));

    return true;
  };

  return (
    <div className="space-y-5 max-w-7xl mx-auto relative">
      {/* Toast Feedback */}
      {toastMsg && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white px-5 py-2.5 rounded-full shadow-2xl font-bold text-xs flex items-center gap-2 animate-in slide-in-from-top-4 fade-in">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
          {toastMsg}
        </div>
      )}

      {/* Cabeçalho do Painel Operacional */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-red-600 text-white rounded-xl shadow-sm">
            <Radio className="h-5 w-5 animate-pulse" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 leading-tight">
              Painel Operacional & Rastreamento
            </h1>
            <p className="text-xs text-slate-500">
              Acompanhamento GPS em tempo real dos motoristas e roteiros em execução.
            </p>
          </div>
        </div>

        <button
          onClick={() => carregarDados(true)}
          disabled={loading}
          className="px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-semibold flex items-center gap-2 transition shadow-sm ml-auto"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          <span>Atualizar</span>
        </button>
      </div>

      {/* Estatísticas Chave */}
      <OperacionalHeaderStats stats={stats} />

      {/* Rastreamento no Mapa */}
      <MapaMotoristasOperacional
        roteiros={filteredRoteiros}
        selectedRoteiroId={selectedRoteiroId}
        onSelectRoteiro={(id) => setSelectedRoteiroId(id)}
      />

      {/* Lista de Roteiros Ativos */}
      <ListaRoteirosAtivos
        roteiros={filteredRoteiros}
        selectedRoteiroId={selectedRoteiroId}
        onSelectRoteiro={(id) => setSelectedRoteiroId(id)}
        onOpenOcorrenciaModal={handleOpenOcorrenciaModal}
        onOpenNotificarModal={handleOpenNotificarModal}
        onGpsSolicitadoFeedback={showToast}
      />

      {/* Modal de Ocorrência Operacional */}
      <OcorrenciaOperacionalModal
        isOpen={isOcorrenciaModalOpen}
        onClose={() => setIsOcorrenciaModalOpen(false)}
        roteiro={roteiroOcorrencia}
        onSalvarOcorrencia={handleSalvarOcorrencia}
      />

      {/* Modal de Notificações / GPS */}
      <NotificarMotoristaModal
        isOpen={isNotificarModalOpen}
        onClose={() => setIsNotificarModalOpen(false)}
        roteiro={roteiroNotificar}
        onNotificacaoEnviada={showToast}
      />
    </div>
  );
}
